import type { CommonProps } from '#/ui/utils/types';
import { cn } from '#/utils/classnames';

export type LoadingVariant = 'spinner' | 'dots' | 'pulse' | 'ball' | 'bars' | 'infinity';

export type LoadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type LoadingColor =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'success'
  | 'error'
  | 'neutral'
  | 'info'
  | 'warning';

export interface LoadingProps extends CommonProps<HTMLDivElement> {
  /**
   * Variant of the loading animation.
   * @default 'spinner'
   */
  variant?: LoadingVariant;
  /**
   * Size of the loading animation.
   * @default 'md'
   */
  size?: LoadingSize;
  /**
   * Color of the loading animation.
   * @default 'primary'
   */
  color?: LoadingColor;
}

function Loading({
  className,
  variant = 'spinner',
  color = 'primary',
  size = 'md',
  ...props
}: LoadingProps) {
  return (
    <span
      {...props}
      className={cn(
        'gooey-loading',
        'd-loading',
        sizeMap[size],
        variantMap[variant],
        colorMap[color],
        className
      )}
    />
  );
}
Loading.displayName = 'GooeyLoading';

export default Loading;

/*
|------------------
| Internals
|------------------
*/

const colorMap = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  accent: 'text-accent',
  success: 'text-success',
  error: 'text-error',
  neutral: 'text-neutral',
  info: 'text-info',
  warning: 'text-warning',
} as const;

const sizeMap = {
  xs: 'd-loading-xs',
  sm: 'd-loading-sm',
  md: 'd-loading-md',
  lg: 'd-loading-lg',
  xl: 'd-loading-xl',
};

const variantMap = {
  spinner: 'd-loading-spinner',
  dots: 'd-loading-dots',
  pulse: 'd-loading-ring',
  ball: 'd-loading-ball',
  bars: 'd-loading-bars',
  infinity: 'd-loading-infinity',
} as const;
